(() => {
  if (customElements.get('facets-container')) return;

  customElements.define(
    'facets-container',
    class FacetsContainer extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.cache = new Map();
        this.requestId = 0;

        this.addEventListener('change', (event) => {
          const input = event.target;
          const form = input.form;
          if (!form || !form.hasAttribute('data-facets-form') || !form.hasAttribute('data-auto-submit')) return;
          if (input.name === 'sort_by') {
            const details = input.closest('details');
            if (details) details.open = false;
          }
          this.submitForm(form);
        });

        this.addEventListener('submit', (event) => {
          const form = event.target;
          if (!form.matches('[data-facets-form]')) return;
          event.preventDefault();
          this.closeDrawer();
          this.submitForm(form, { focusResults: true });
        });

        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-facets-link], .pagination a');
          if (!link || !this.contains(link)) return;
          event.preventDefault();
          const isPagination = Boolean(link.closest('.pagination'));
          const inDrawer = Boolean(link.closest('drawer-component'));
          if (inDrawer) this.closeDrawer();
          const url = new URL(link.href, window.location.origin);
          this.render(url.search, { push: true, scroll: isPagination, focusResults: isPagination || inDrawer });
        });

        this.onPopState = () => this.render(window.location.search, { push: false });
        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
      }

      get drawer() {
        return this.querySelector('drawer-component');
      }

      closeDrawer() {
        const drawer = this.drawer;
        if (drawer && drawer.isOpen) drawer.close();
      }

      serialize(form) {
        const params = new URLSearchParams();
        for (const [key, value] of new FormData(form).entries()) {
          if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
        }
        return params.toString();
      }

      submitForm(form, options = {}) {
        this.render(`?${this.serialize(form)}`, { push: true, ...options });
      }

      buildQuery(search) {
        const params = new URLSearchParams(search.replace(/^\?/, ''));
        params.delete('section_id');
        return params.toString();
      }

      async fetchSection(query) {
        if (this.cache.has(query)) return this.cache.get(query);
        const url = `${window.location.pathname}?${query}${query ? '&' : ''}section_id=${encodeURIComponent(this.sectionId)}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        this.cache.set(query, html);
        return html;
      }

      async render(search, { push = true, scroll = false, focusResults = false } = {}) {
        const query = this.buildQuery(search);
        const requestId = ++this.requestId;
        const results = this.querySelector('[data-facets-replace="results"]');
        const active = document.activeElement;
        const activeId = active && this.contains(active) && !active.closest('drawer-component') ? active.id : null;
        const openGroups = Array.from(this.querySelectorAll('details[open][id]')).map((d) => d.id);

        if (results) {
          results.setAttribute('aria-busy', 'true');
          results.classList.add('is-loading');
        }

        let html;
        try {
          html = await this.fetchSection(query);
        } catch (error) {
          window.location.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
          return;
        }
        if (requestId !== this.requestId) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.getElementById(this.id) || doc;

        this.querySelectorAll('[data-facets-replace]').forEach((target) => {
          const key = target.dataset.facetsReplace;
          const fresh = source.querySelector(`[data-facets-replace="${key}"]`);
          if (!fresh) return;
          if (key === 'drawer') {
            target.innerHTML = fresh.innerHTML;
          } else {
            target.replaceWith(fresh);
          }
        });

        openGroups.forEach((id) => {
          const details = document.getElementById(id);
          if (details && this.contains(details)) details.open = true;
        });

        if (push) {
          window.history.pushState({ facets: this.sectionId }, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        }

        if (window.theme && window.theme.initReveal) window.theme.initReveal(this);

        const freshResults = this.querySelector('[data-facets-replace="results"]');
        if (scroll && freshResults) {
          const reduce = window.theme && window.theme.prefersReducedMotion && window.theme.prefersReducedMotion();
          freshResults.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }

        const toFocus = activeId ? document.getElementById(activeId) : null;
        if (toFocus && this.contains(toFocus)) {
          toFocus.focus({ preventScroll: true });
        } else if ((focusResults || activeId !== null || (active && this.contains(active))) && freshResults) {
          freshResults.focus({ preventScroll: !scroll });
        }

        const count =
          this.querySelector('[data-facets-replace="count"]') ||
          this.querySelector('[data-facets-replace="summary"]') ||
          this.querySelector('.facets-empty');
        if (count && window.theme && window.theme.announce) window.theme.announce(count.textContent.trim());
      }
    }
  );
})();
